import React from "react";
import { motion } from "framer-motion";
import { Check, Minus, Crown } from "lucide-react";

// True = full support · "limited" = partial · false = not available
const rows = [
  { feature: "Onboarding Support", values: { Wappit: "Dedicated", WATI: "Email", Interakt: "Email", AiSensy: "Self-serve", DoubleTick: "Email" } },
  { feature: "Setup Assistance", values: { Wappit: true, WATI: "limited", Interakt: "limited", AiSensy: false, DoubleTick: "limited" } },
  { feature: "Shared Team Inbox", values: { Wappit: true, WATI: true, Interakt: true, AiSensy: true, DoubleTick: true } },
  { feature: "Chatbot Builder", values: { Wappit: true, WATI: true, Interakt: "limited", AiSensy: true, DoubleTick: "limited" } },
  { feature: "Multi-Agent Access", values: { Wappit: "Unlimited*", WATI: "Add-on", Interakt: "Add-on", AiSensy: "Add-on", DoubleTick: "Add-on" } },
  { feature: "Human Support (Phone + WhatsApp)", values: { Wappit: "9 AM – 7 PM", WATI: "Email only", Interakt: "Email only", AiSensy: "Email only", DoubleTick: "Email only" } },
  { feature: "Pricing Transparency", values: { Wappit: true, WATI: "limited", Interakt: "limited", AiSensy: true, DoubleTick: "limited" } },
];

const cols = ["Wappit", "WATI", "Interakt", "AiSensy", "DoubleTick"];

const Cell = ({ value, highlight }) => {
  if (value === true) {
    return (
      <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${highlight ? "bg-emerald-500 text-white" : "bg-emerald-100 text-emerald-700"}`}>
        <Check className="h-4 w-4" />
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-400">
        <Minus className="h-4 w-4" />
      </span>
    );
  }
  if (value === "limited") {
    return <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-100 px-2 py-1 rounded-full">Limited</span>;
  }
  return (
    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${highlight ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-700"}`}>
      {value}
    </span>
  );
};

export const Comparison = () => {
  return (
    <section id="comparison" className="py-20 sm:py-28 bg-white overflow-hidden" data-testid="comparison-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-emerald-600">How we compare</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Wappit vs the rest
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Independent comparison of the most popular WhatsApp Business API platforms for Indian businesses.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden"
        >
          {/* Scroll hint shown only on small screens */}
          <p className="sm:hidden text-[11px] text-slate-400 text-right px-4 pt-3 pb-1 font-medium">← Scroll to compare →</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]" data-testid="comparison-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-4">Feature</th>
                  {cols.map((c) => (
                    <th
                      key={c}
                      data-testid={`comparison-col-${c.toLowerCase()}`}
                      className={`text-center text-xs font-bold uppercase tracking-wider px-3 sm:px-5 py-4 ${
                        c === "Wappit" ? "text-emerald-700 bg-emerald-50/60" : "text-slate-500"
                      }`}
                    >
                      <div className="inline-flex items-center gap-1.5">
                        {c === "Wappit" && <Crown className="h-3.5 w-3.5" />}
                        {c}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.feature}>
                    <td className="px-5 py-4 font-semibold text-slate-800">{r.feature}</td>
                    {cols.map((c) => (
                      <td
                        key={c}
                        className={`text-center px-3 sm:px-5 py-4 ${c === "Wappit" ? "bg-emerald-50/40" : ""}`}
                      >
                        <Cell value={r.values[c]} highlight={c === "Wappit"} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-100">
            * Subject to fair-usage policy. Comparison data based on publicly available information as of 2026. Sources can be shared on request.
          </div>
        </motion.div>
      </div>
    </section>
  );
};
